import { Fragment } from 'react'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'

import type { Field } from '../../api/services'
import { fieldLabel, typeLabel } from './contract'
import { Ltr } from '../../components/ui/Ltr'

interface Props {
  fields: readonly Field[]
  /** Whether to show the «الزامی» column — meaningless for a result. */
  showRequired: boolean
  caption: string
}

/**
 * One half of a capability's contract as a table.
 *
 * Nested children are rendered as indented rows rather than as a second table,
 * because a list of cheques and the fields of one cheque are one contract read
 * top to bottom. Indentation is applied as padding on the leading edge, which
 * the RTL stylesheet mirrors, so the tree reads the right way round.
 */
export function ContractTable({ fields, showRequired, caption }: Props) {
  if (fields.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        این سرویس هیچ فیلدی در این بخش ندارد.
      </Typography>
    )
  }

  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" aria-label={caption}>
        <TableHead>
          <TableRow>
            <TableCell>نام فیلد</TableCell>
            <TableCell>معنی</TableCell>
            <TableCell>نوع</TableCell>
            {showRequired ? <TableCell>الزامی</TableCell> : null}
          </TableRow>
        </TableHead>
        <TableBody>
          <Rows fields={fields} showRequired={showRequired} depth={0} />
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function Rows({
  fields,
  showRequired,
  depth,
}: {
  fields: readonly Field[]
  showRequired: boolean
  depth: number
}) {
  return (
    <>
      {fields.map((field) => (
        <Fragment key={`${depth}-${field.name}`}>
          <TableRow hover>
            <TableCell sx={{ paddingInlineStart: depth * 3 + 2 }}>
              <Ltr component="code" sx={{ fontFamily: 'monospace' }}>
                {field.name}
              </Ltr>
            </TableCell>
            <TableCell>{fieldLabel(field.name)}</TableCell>
            <TableCell>
              {typeLabel(field.type)}
              {field.nullable ? (
                <Typography variant="caption" color="text.secondary" component="span">
                  {' '}
                  (می‌تواند خالی باشد)
                </Typography>
              ) : null}
            </TableCell>
            {showRequired ? <TableCell>{field.required ? 'بله' : 'خیر'}</TableCell> : null}
          </TableRow>
          {field.children && field.children.length > 0 ? (
            <Rows fields={field.children} showRequired={showRequired} depth={depth + 1} />
          ) : null}
        </Fragment>
      ))}
    </>
  )
}
